// ═══════════════════════════════════════════════════════════════════════════
// FLUSSO ACQUISTI: RICHIESTA (NF) → ORDINE (ODA) — logica pura (ottobre 2026)
// ═══════════════════════════════════════════════════════════════════════════
// Regole decise con Paolo:
//   • stati della NF (W435), calcolati dai dati e mai scritti a mano (statoNf):
//     Bozza → Da gestire → In gestione → Ordinata → Consegnata, oppure Annullata;
//   • la NF la modifica e la elimina SOLO il suo autore, finché è Bozza o Da gestire;
//     dopo si annulla con un motivo (resta nello storico) e una correzione si chiede in chat;
//   • «Prendi in gestione» è un pulsante esplicito dell'ufficio Acquisti (anche creare
//     l'ODA o scrivere nelle colonne degli Acquisti la prende in gestione);
//   • una NF = un ODA (nessun raggruppamento di più NF in un ordine);
//   • l'ODA nasce da un pulsante esplicito ("Crea ODA") GIÀ COMPILATO con i dati della NF;
//   • NF e ODA si vedono a vicenda: la NF porta odaId/odaNumero/odaStato, l'ODA nfId/nfNumero.
// ═══════════════════════════════════════════════════════════════════════════

export const nuovoArticolo = () => ({
  id: `a${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  descrizione: "", codice: "", quantita: "", unitaMisura: "", prezzoIndicativo: "",
});

/** Articoli compilati davvero (descrizione presente), ripuliti. */
export function articoliValidi(articoli) {
  return (articoli || [])
    .filter(a => String(a?.descrizione || "").trim())
    .map(a => ({
      id: a.id || nuovoArticolo().id,
      descrizione: String(a.descrizione).trim(),
      codice: String(a.codice || "").trim(),
      quantita: String(a.quantita || "").trim(),
      unitaMisura: String(a.unitaMisura || "").trim(),
      prezzoIndicativo: String(a.prezzoIndicativo || "").trim(),
    }));
}

/** Stati della NF, in ordine di avanzamento. */
export const STATI_NF = ["Bozza", "Da gestire", "In gestione", "Ordinata", "Consegnata", "Annullata"];
export const STATO_NF_COLORI = {
  "Bozza":       { bg: "#f3f4f6", text: "#6b7280", icona: "📝" },
  "Da gestire":  { bg: "#fee2e2", text: "#b91c1c", icona: "📥" },
  "In gestione": { bg: "#ede9fe", text: "#6d28d9", icona: "✋" },
  "Ordinata":    { bg: "#dbeafe", text: "#1d4ed8", icona: "🛒" },
  "Consegnata":  { bg: "#d1fae5", text: "#047857", icona: "✅" },
  "Annullata":   { bg: "#f3f4f6", text: "#9ca3af", icona: "✕" },
};
const ODA_CONSEGNATO = ["Ricevuto", "Fattura verificata", "Chiuso"];

/** L'ordine collegato alla NF esiste ed è valido (non annullato)? */
export const haOdaValido = (nf) => !!(nf?.odaId || nf?.odaNumero) && nf?.odaStato !== "Annullato";

/** Stato della NF, sempre calcolato dai dati: nessuno lo scrive a mano. */
export function statoNf(nf) {
  if (!nf) return "Bozza";
  if (nf.annullata) return "Annullata";
  if ((nf.statoRichiesta || "Da inviare") === "Da inviare") return "Bozza";
  if (haOdaValido(nf)) return ODA_CONSEGNATO.includes(nf.odaStato) ? "Consegnata" : "Ordinata";
  if (nf.presaInCaricoDa) return "In gestione";
  return "Da gestire";
}

/** La NF conta ancora (non eliminata né annullata)? Per elenchi, doppioni e conteggi. */
export const nfAttiva = (nf) => !!nf && !nf.eliminata && !nf.annullata;

/** La NF si può ancora modificare (ed eliminare) da questo utente? Solo l'autore, finché è Bozza o Da gestire. */
export function nfModificabileDa(nf, uid) {
  return !!nf && !!uid && nf.createdBy === uid && !nf.eliminata && ["Bozza", "Da gestire"].includes(statoNf(nf));
}
export const nfEliminabileDa = nfModificabileDa;

/** L'ufficio Acquisti può annullarla? Da «Da gestire» in poi, finché non c'è un ordine valido. */
export const nfAnnullabile = (nf) => ["Da gestire", "In gestione"].includes(statoNf(nf));

// ── Descrizione scritta di fretta → articoli ─────────────────────────────────
// Una voce per riga; quantità e unità di misura riconosciute solo all'inizio della riga:
// «10 m cavo FG16OR16 3x2,5» → 10 · m · cavo FG16OR16 3x2,5; «n. 5 scatole 503» → 5 · pz;
// «2 x interruttore» → 2 · pz. Il resto resta nella descrizione, da controllare prima di salvare.
const UNITA = { pz: "pz", pezzi: "pz", pezzo: "pz", n: "pz", nr: "pz", num: "pz", m: "m", mt: "m", ml: "m", metri: "m", metro: "m",
  m2: "m²", mq: "m²", "m²": "m²", m3: "m³", mc: "m³", "m³": "m³", kg: "kg", g: "g", l: "l", lt: "l", litri: "l",
  conf: "conf", cf: "conf", confezioni: "conf", confezione: "conf", rot: "rotoli", rotolo: "rotoli", rotoli: "rotoli",
  bob: "bobine", bobina: "bobine", bobine: "bobine", cad: "pz", set: "set", kit: "kit", ore: "h", h: "h" };
export function articoliDaTesto(testo) {
  return String(testo || "").split(/\r?\n/).map(r => r.trim()).filter(Boolean).map(riga => {
    let r = riga.replace(/^([-•*·]|\d{1,3}[).]\s)\s*/, "").trim();
    let quantita = "", unitaMisura = "";
    const mq = r.match(/^(?:n\.?\s*|nr\.?\s*)?(\d+(?:[.,]\d+)?)\s*(x\s+|×\s*)?([a-zA-Z²³]+\.?)?\s+(.+)$/i);
    if (mq) {
      const u = (mq[3] || "").replace(/\.$/, "").toLowerCase();
      if (mq[2]) { quantita = mq[1]; unitaMisura = "pz"; r = `${mq[3] ? mq[3] + " " : ""}${mq[4]}`.trim(); }
      else if (u && UNITA[u]) { quantita = mq[1]; unitaMisura = UNITA[u]; r = mq[4].trim(); }
      else if (!u) { quantita = mq[1]; unitaMisura = "pz"; r = mq[4].trim(); }
      else if (/^n\.?|^nr/i.test(riga)) { quantita = mq[1]; unitaMisura = "pz"; r = `${mq[3]} ${mq[4]}`.trim(); }
    }
    return { ...nuovoArticolo(), descrizione: r, quantita: quantita.replace(".", ","), unitaMisura };
  });
}
/** La descrizione ha più voci (più righe)? Allora conviene trasformarla in articoli. */
export const descrizioneConPiuRighe = (testo) => String(testo || "").split(/\r?\n/).filter(r => r.trim()).length >= 2;

// ── Copia per la richiesta d'offerta al fornitore ────────────────────────────
// Solo quello che serve al fornitore: articoli (descrizione, codice, quantità, U.M.) oppure la
// descrizione così com'è, più luogo e data di consegna. Mai prezzo indicativo, commessa,
// richiedente o importi: sono dati interni.
const dataIt = (iso) => (iso ? new Date(iso + "T00:00:00").toLocaleDateString("it-IT") : "");
export function luogoConsegnaNf(nf) {
  if (!nf?.destinazione) return "";
  if (nf.destinazione === "Ns sede") return "presso la nostra sede";
  if (nf.destinazione === "Cantiere") return "in cantiere";
  if (nf.destinazione === "Ritiro presso fornitore") return "ritiro presso di voi";
  return nf.destinazione;
}
export function copiaPerFornitore(nf) {
  const art = articoliValidi(nf?.articoli);
  const cons = [];
  const luogo = luogoConsegnaNf(nf);
  if (luogo) cons.push(["Consegna", luogo + (nf.destinazione === "Misto" && nf.dettaglioDestinazioneMista ? `: ${nf.dettaglioDestinazioneMista}` : "")]);
  if (nf?.dataConsegna) cons.push(["Data di consegna richiesta", dataIt(nf.dataConsegna)]);
  const righeTesto = [];
  let html = "";
  const td = 'style="border:1px solid #999;padding:4px 8px;vertical-align:top"';
  if (art.length) {
    righeTesto.push(["Descrizione", "Codice", "Q.tà", "U.M."].join("\t"));
    art.forEach(a => righeTesto.push([a.descrizione, a.codice, a.quantita, a.unitaMisura].map(x => String(x || "").replace(/[\t\n]+/g, " ")).join("\t")));
    html = `<table style="border-collapse:collapse;font-family:Arial,sans-serif;font-size:13px"><thead><tr>${["Descrizione", "Codice", "Q.tà", "U.M."].map(h => `<th ${td.replace('"', '"background:#eee;text-align:left;')}>${h}</th>`).join("")}</tr></thead><tbody>${
      art.map(a => `<tr><td ${td}>${esc(a.descrizione)}</td><td ${td}>${esc(a.codice)}</td><td ${td.replace('"', '"text-align:right;')}>${esc(a.quantita)}</td><td ${td}>${esc(a.unitaMisura)}</td></tr>`).join("")}</tbody></table>`;
  } else {
    const d = String(nf?.descrizione || "").trim();
    righeTesto.push(d);
    html = `<div style="font-family:Arial,sans-serif;font-size:13px;white-space:pre-wrap">${esc(d)}</div>`;
  }
  if (cons.length) {
    righeTesto.push("");
    cons.forEach(([k, v]) => righeTesto.push(`${k}: ${v}`));
    html += `<p style="font-family:Arial,sans-serif;font-size:13px;margin-top:10px">${cons.map(([k, v]) => `<b>${esc(k)}:</b> ${esc(v)}`).join("<br>")}</p>`;
  }
  return { testo: righeTesto.join("\n"), html };
}

// ── Righe dell'ODA confrontate con la NF ─────────────────────────────────────
const chiaveRiga = (r) => ["descrizione", "codice", "quantita", "unitaMisura"].map(k => String(r?.[k] ?? "").trim().toLowerCase().replace(",", ".")).join("|");
/** Differenze tra le righe dell'ordine e la richiesta (prezzi esclusi: cambiano sempre). */
export function differenzeOdaNf(righe = [], righeNf = []) {
  const valide = (righe || []).filter(r => String(r?.descrizione || "").trim());
  const perRiga = {};
  let aggiunte = 0, modificate = 0;
  valide.forEach(r => {
    if (!r.daNf) { perRiga[r.id] = "aggiunta"; aggiunte++; }
    else if (chiaveRiga(r) !== chiaveRiga(r.daNf)) { perRiga[r.id] = "modificata"; modificate++; }
  });
  const presenti = new Set(valide.filter(r => r.daNf).map(r => r.daNf.id));
  const tolte = (righeNf || []).filter(o => !presenti.has(o.id)).length;
  return { aggiunte, modificate, tolte, perRiga, diversa: aggiunte + modificate + tolte > 0 };
}
/** «2 righe aggiunte, 1 modificata, 1 tolta» ("" se l'ordine è uguale alla richiesta). */
export function testoDifferenze(d) {
  if (!d || !(d.aggiunte || d.modificate || d.tolte)) return "";
  const p = (n, s, pl) => `${n} ${n === 1 ? s : pl}`;
  return [d.aggiunte ? p(d.aggiunte, "riga aggiunta", "righe aggiunte") : "", d.modificate ? p(d.modificate, "modificata", "modificate") : "",
    d.tolte ? p(d.tolte, "tolta", "tolte") : ""].filter(Boolean).join(", ");
}

/** Righe dell'ODA a partire dalla NF: gli articoli se ci sono, altrimenti la descrizione. */
export function righeOdaDaNf(nf) {
  const base = Date.now();
  return righeNfOrigine(nf).map((o, i) => ({
    id: `r${base}_${i}`, descrizione: o.descrizione, codice: o.codice, quantita: o.quantita, unitaMisura: o.unitaMisura,
    prezzoUnitario: o.prezzoIndicativo || "", sconto: "", iva: "22", daNf: o,
  }));
}

/** Le righe come le ha chieste la NF (riferimento fisso per il confronto con l'ordine). */
export function righeNfOrigine(nf) {
  const art = articoliValidi(nf?.articoli);
  if (art.length) return art.map(a => ({ id: a.id, descrizione: a.descrizione, codice: a.codice, quantita: a.quantita || "1", unitaMisura: a.unitaMisura, prezzoIndicativo: a.prezzoIndicativo }));
  return [{ id: "descrizione", descrizione: String(nf?.descrizione || "").trim(), codice: "", quantita: "1", unitaMisura: "", prezzoIndicativo: "" }];
}

/** Note dell'ordine composte dai dati utili della NF (consegna, offerta, urgenza). */
export function noteOdaDaNf(nf) {
  const parti = [];
  if (nf?.destinazione) parti.push(`Consegna: ${nf.destinazione}${nf.destinazione === "Misto" && nf.dettaglioDestinazioneMista ? ` (${nf.dettaglioDestinazioneMista})` : ""}`);
  if (nf?.rifOfferta) parti.push(`Rif. offerta: ${nf.rifOfferta}`);
  if (nf?.urgenza === "Urgente") parti.push(`URGENTE${nf.motivoUrgenza ? `: ${nf.motivoUrgenza}` : ""}`);
  return parti.join(" · ");
}

/** Dati del nuovo ODA (senza timestamp: li aggiunge chi scrive). */
export function odaDaNf(nf, { numeroODA, orgId, uid, oggi }) {
  return {
    nfId: nf.id, nfNumero: nf.numero || "", nfDescrizione: nf.descrizione || "", nfCompilatore: nf.compilatoreNome || "",
    numeroODA,
    fornitoreId: nf.fornitoreId || "",
    fornitoreNome: nf.fornitoreId ? (nf.fornitoreNome || "") : "",
    commessaId: nf.commessaId || "",
    commessaNome: nf.commessaNome || nf.commessaLibera || "",
    righe: righeOdaDaNf(nf),
    righeNf: righeNfOrigine(nf),
    descrizione: nf.descrizione || "",
    note: noteOdaDaNf(nf),
    dataConsegnaPrevista: nf.dataConsegna || "",
    linkOfferta: nf.linkOfferta || "",
    allegatoOfferta: nf.allegatoOfferta || null,
    organizationId: orgId,
    data: oggi,
    stato: "Bozza",
    createdBy: uid || "",
  };
}

/** Etichetta breve dello stato dell'ordine per chi ha fatto la richiesta. */
export function etichettaNfOrdinata(nf) {
  if (!haOdaValido(nf)) return "";
  return `ODA n. ${nf.odaNumero || "—"}${nf.odaStato ? ` · ${nf.odaStato}` : ""}`;
}

const esc = (t) => String(t ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
/** Tabella HTML degli articoli per il PDF della NF ("" se non ce ne sono). */
export function htmlArticoliNF(nf) {
  const art = articoliValidi(nf?.articoli);
  if (!art.length) return "";
  return `<div style="margin-bottom:12px"><span class="lbl">Articoli richiesti</span>
    <table style="width:100%;border-collapse:collapse;font-size:11px;margin-top:4px">
      <tr style="background:#f3f4f6;text-align:left"><th style="padding:5px 6px">Descrizione</th><th style="padding:5px 6px">Codice</th><th style="padding:5px 6px">Q.tà</th><th style="padding:5px 6px">U.M.</th><th style="padding:5px 6px">Prezzo indic.</th></tr>
      ${art.map(a => `<tr style="border-top:1px solid #e5e7eb"><td style="padding:5px 6px">${esc(a.descrizione)}</td><td style="padding:5px 6px">${esc(a.codice) || "—"}</td><td style="padding:5px 6px">${esc(a.quantita) || "—"}</td><td style="padding:5px 6px">${esc(a.unitaMisura) || "—"}</td><td style="padding:5px 6px">${a.prezzoIndicativo ? "€ " + esc(a.prezzoIndicativo) : "—"}</td></tr>`).join("")}
    </table></div>`;
}
